import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { validateDateParams } from '@/dashboards/utils/validateDateAndPeriodSearchParams';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { createContext, useContext } from 'use-context-selector';

import { EXECUTIVE_SUMMARY_COMPARISON_CONFIG } from '../config/comparison';
import { ExecutiveSummarySearch } from '../config/search';
import {
    DEFAULT_EXECUTIVE_SUMMARY_PRESET,
    ExecutiveSummaryPreset,
    ExecutiveSummaryPresetOrCustom,
} from '../config/presets';
import { buildDateRangePatch } from '../helpers/buildDateRangePatch';
import { computePresetRanges, DateRangeStrings } from '../helpers/computePresetRanges';
import { useExecutiveSummaryUrl } from '../hooks';

interface ExecutiveSummaryContextValue {
    /** Selected primary range, `'DD-MM-YYYY'`. */
    primaryRange: DateRangeStrings;
    /** Comparison range, or `null` when comparison is disabled. */
    comparisonRange: DateRangeStrings | null;
    compareEnabled: boolean;
    /** Whether the Compare on/off toggle should be rendered (presentation only). */
    showCompareToggle: boolean;
    preset: ExecutiveSummaryPresetOrCustom;
    /** Project's available-data bounds, `'DD-MM-YYYY'`. */
    bounds: DateRangeStrings;
    /** Apply a quick-select preset (recomputes both ranges, enables compare). */
    setPreset: (preset: ExecutiveSummaryPreset) => void;
    setCompareEnabled: (enabled: boolean) => void;
    /** Hand-edit the primary range; flips the active preset to `custom`. */
    setPrimaryRange: (range: {
        startDate?: string;
        endDate?: string;
    }) => void;
    /** Hand-edit the comparison range; flips the active preset to `custom`. */
    setComparisonRange: (range: {
        startDate?: string;
        endDate?: string;
    }) => void;
}

export const ExecutiveSummaryContext =
    createContext<ExecutiveSummaryContextValue>(
        null as unknown as ExecutiveSummaryContextValue,
    );

/**
 * sessionStorage key under which the last-used selection is mirrored so it
 * survives navigating away to another dashboard and back.
 */
const STORAGE_KEY = 'executiveSummarySearch';

type PersistedSearch = Pick<
    ExecutiveSummarySearch,
    | 'startDate'
    | 'endDate'
    | 'compareStartDate'
    | 'compareEndDate'
    | 'compareEnabled'
    | 'preset'
>;

const readStoredSearch = (): PersistedSearch => {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as PersistedSearch) : {};
    } catch {
        return {};
    }
};

/**
 * Coerces a stored range back inside the project bounds. Falls back to the
 * supplied default range when a value is missing or out of bounds.
 */
const resolveRange = (
    range: { startDate?: string; endDate?: string },
    fallback: DateRangeStrings,
    bounds: DateRangeStrings,
): DateRangeStrings => {
    const startDate = range.startDate ?? fallback.startDate;
    const endDate = range.endDate ?? fallback.endDate;

    const corrected = validateDateParams({
        boundaryStartDate: bounds.startDate,
        boundaryEndDate: bounds.endDate,
        startDate,
        endDate,
    });

    return corrected ?? { startDate, endDate };
};

export const ExecutiveSummaryContextProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const { search, setSearch } = useExecutiveSummaryUrl();
    const { startDate: boundaryStartDate, endDate: boundaryEndDate } =
        useDashboardGlobalContext();

    // Mirror every selection into sessionStorage so it survives navigating to
    // another dashboard and back. All setters below route through this wrapper.
    const persistSearch = useCallback(
        (next: PersistedSearch) => {
            setSearch(next);
            try {
                window.sessionStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify({ ...readStoredSearch(), ...next }),
                );
            } catch {
                // sessionStorage unavailable (e.g. private mode) — ignore.
            }
        },
        [setSearch],
    );

    // Re-hydrate from sessionStorage once on mount: when the URL carries no
    // explicit selection (fresh navigation to the dashboard) but a previous
    // selection was stored, restore it into the URL.
    useEffect(() => {
        const hasUrlSelection =
            search.startDate !== undefined ||
            search.endDate !== undefined ||
            search.compareStartDate !== undefined ||
            search.compareEndDate !== undefined ||
            search.compareEnabled !== undefined ||
            search.preset !== undefined;

        if (hasUrlSelection) {
            return;
        }

        const stored = readStoredSearch();
        if (Object.keys(stored).length > 0) {
            setSearch(stored);
        }
        // Mount-only: we intentionally restore just the initial navigation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const bounds: DateRangeStrings = useMemo(
        () => ({ startDate: boundaryStartDate, endDate: boundaryEndDate }),
        [boundaryStartDate, boundaryEndDate],
    );

    const preset: ExecutiveSummaryPresetOrCustom =
        search.preset ?? DEFAULT_EXECUTIVE_SUMMARY_PRESET;
    const compareEnabled =
        search.compareEnabled ??
        EXECUTIVE_SUMMARY_COMPARISON_CONFIG.defaultEnabled;

    // Defaults derived from the active (or default) preset, used whenever the
    // URL has no explicit dates yet.
    const defaultRanges = useMemo(
        () =>
            computePresetRanges({
                preset:
                    preset === 'custom'
                        ? DEFAULT_EXECUTIVE_SUMMARY_PRESET
                        : preset,
                boundaryStartDate,
                boundaryEndDate,
            }),
        [preset, boundaryStartDate, boundaryEndDate],
    );

    const primaryRange = useMemo(
        () =>
            resolveRange(
                { startDate: search.startDate, endDate: search.endDate },
                defaultRanges.primary,
                bounds,
            ),
        [search.startDate, search.endDate, defaultRanges.primary, bounds],
    );

    const comparisonRange = useMemo(() => {
        if (!compareEnabled) {
            return null;
        }
        return resolveRange(
            {
                startDate: search.compareStartDate,
                endDate: search.compareEndDate,
            },
            defaultRanges.comparison,
            bounds,
        );
    }, [
        compareEnabled,
        search.compareStartDate,
        search.compareEndDate,
        defaultRanges.comparison,
        bounds,
    ]);

    const setPreset = useCallback(
        (nextPreset: ExecutiveSummaryPreset) => {
            const ranges = computePresetRanges({
                preset: nextPreset,
                boundaryStartDate,
                boundaryEndDate,
            });
            persistSearch({
                preset: nextPreset,
                compareEnabled: true,
                startDate: ranges.primary.startDate,
                endDate: ranges.primary.endDate,
                compareStartDate: ranges.comparison.startDate,
                compareEndDate: ranges.comparison.endDate,
            });
        },
        [boundaryStartDate, boundaryEndDate, persistSearch],
    );

    const setCompareEnabled = useCallback(
        (enabled: boolean) => {
            persistSearch({ compareEnabled: enabled });
        },
        [persistSearch],
    );

    const setPrimaryRange = useCallback(
        (range: { startDate?: string; endDate?: string }) => {
            persistSearch({
                ...buildDateRangePatch(range, {
                    start: 'startDate',
                    end: 'endDate',
                }),
                preset: 'custom',
            });
        },
        [persistSearch],
    );

    const setComparisonRange = useCallback(
        (range: { startDate?: string; endDate?: string }) => {
            persistSearch({
                ...buildDateRangePatch(range, {
                    start: 'compareStartDate',
                    end: 'compareEndDate',
                }),
                preset: 'custom',
            });
        },
        [persistSearch],
    );

    const value: ExecutiveSummaryContextValue = useMemo(
        () => ({
            primaryRange,
            comparisonRange,
            compareEnabled,
            showCompareToggle: EXECUTIVE_SUMMARY_COMPARISON_CONFIG.showToggle,
            preset,
            bounds,
            setPreset,
            setCompareEnabled,
            setPrimaryRange,
            setComparisonRange,
        }),
        [
            primaryRange,
            comparisonRange,
            compareEnabled,
            preset,
            bounds,
            setPreset,
            setCompareEnabled,
            setPrimaryRange,
            setComparisonRange,
        ],
    );

    return (
        <ExecutiveSummaryContext.Provider value={value}>
            {children}
        </ExecutiveSummaryContext.Provider>
    );
};

export const useExecutiveSummaryContext = () => {
    const context = useContext(ExecutiveSummaryContext);

    if (!context) {
        throw new Error(
            'useExecutiveSummaryContext must be used within an ExecutiveSummaryContextProvider',
        );
    }

    return context;
};
